/**
 * Purpose: Custom hook for loading comments for a thread
 *
 * How it works:
 * 1. Sets up an AbortController to handle cancellation
 * 2. Progressively loads comments for the active thread
 * 3. Handles error cases with toast notifications
 * 4. Cleans up by aborting requests when unmounting
 *
 * Key features:
 * - Automatic cancellation on unmount or chat change
 * - Error handling with user-friendly notifications
 * - Proper signal handling for API requests
 */

import { useEffect } from "react";

import { useToast } from "@/hooks/useToast";
import { useTranslation } from "react-i18next";
// import { progressivelyLoadThreadComments } from "@/middleware/threads";
import { umbra_progressivelyLoadThreadComments } from "@/middleware/umbra_threads";

/**
 * Hook to load comments for a specific thread
 * @param activeChatId | null
 * @param activeThreadId | null
 */
export function useThreadCommentsLoader(
  activeChatId: string | null,
  activeThreadId: string | null,
) {
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const msgInit = async () => {
      try {
        if (!activeChatId || !activeThreadId) {
          return;
        }

        // await progressivelyLoadThreadComments(
        //   activeChatId,
        //   activeThreadId,
        //   1000,
        //   signal,
        // );

        await umbra_progressivelyLoadThreadComments(
          activeChatId,
          activeThreadId,
          1000,
          signal,
        );
      } catch (error) {
        if (
          error instanceof Error &&
          error.message &&
          error.message === "canceled"
        ) {
          return;
        }
        toast({
          duration: 1000,
          description:
            error instanceof Error ? error.message : t("errorOccurred"),
          variant: "destructive",
        });
      }
    };

    msgInit();

    return () => {
      // Cancel the request when the component unmounts
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId, toast]);
}
